import {
  DestinationEntityStatus,
  IDestinationEntity,
  IDestinationEntityHandler,
  IDestinationHandler,
  ISourceEntityHandler,
  SourceEntityStatus,
  type IPracticeMigration,
} from '@principle-theorem/practice-migrations/interfaces';
import {
  asyncForEach,
  Firestore,
  multiFilter,
  multiMap,
  orderBy,
  safeCombineLatest,
  snapshot,
  type WithRef,
} from '@principle-theorem/shared';
import { compact, flatten, values } from 'lodash';
import { Observable, of } from 'rxjs';
import { map } from 'rxjs/operators';
import { CorePracticeDestinationHandler } from '../core-practice/destination';
import { D4WDestinationHandler } from '../d4w/destination';
import { DentrixDestinationHandler } from '../dentrix/destination';
import { ExactDestinationHandler } from '../exact/destination';
import { ExactDestinationHandlerV2 } from '../exact/destination-v2';
import { OasisDestinationHandler } from '../oasis/destination';
import { PracticeMigration } from '../practice-migrations';
import { PraktikaDestinationHandler } from '../praktika/destination';
import { PracticeWorksDestinationHandler } from '../practice-works/destination';

export const DESTINATION_HANDLERS: IDestinationHandler[] = [
  new CorePracticeDestinationHandler(),
  new D4WDestinationHandler(),
  new PraktikaDestinationHandler(),
  new ExactDestinationHandler(),
  new ExactDestinationHandlerV2(),
  new DentrixDestinationHandler(),
  new OasisDestinationHandler(),
  new PracticeWorksDestinationHandler(),
];

export class DestinationHandler {
  static getDestinationHandler(
    migration: WithRef<IPracticeMigration>
  ): IDestinationHandler | undefined {
    return DESTINATION_HANDLERS.find((destinationHandler) => {
      if (
        destinationHandler.migrationType !== migration.metadata.name ||
        destinationHandler.migrationVersion !== migration.metadata.version
      ) {
        return false;
      }
      return destinationHandler.canHandle(migration.destination);
    });
  }

  static async initialiseDestinationEntities(
    migration: WithRef<IPracticeMigration>
  ): Promise<void> {
    const handler = DestinationHandler.getDestinationHandler(migration);
    if (!handler) {
      return;
    }

    await handler.addEntities(migration);
  }

  static getDestinationEntityHandler(
    migration: WithRef<IPracticeMigration>,
    destinationEntity: IDestinationEntity
  ): IDestinationEntityHandler | undefined {
    const destinationHandler =
      DestinationHandler.getDestinationHandler(migration);
    if (!destinationHandler) {
      return;
    }

    return destinationHandler.entityHandlers.find((entityHandler) =>
      entityHandler.canHandle(destinationEntity)
    );
  }

  static getDestinationEntityHandlerByKey(
    migration: WithRef<IPracticeMigration>,
    destinationEntityKey: string
  ): IDestinationEntityHandler | undefined {
    const destinationHandler =
      DestinationHandler.getDestinationHandler(migration);
    if (!destinationHandler) {
      return;
    }

    return destinationHandler.entityHandlers.find(
      (entityHandler) =>
        entityHandler.destinationEntity.metadata.key === destinationEntityKey
    );
  }

  static getDestinationEntityMigrationSourceBlockers$(
    migration: WithRef<IPracticeMigration>,
    destinationEntity: IDestinationEntity
  ): Observable<string[]> {
    const destinationEntityHandler =
      DestinationHandler.getDestinationEntityHandlerByKey(
        migration,
        destinationEntity.metadata.key
      );

    if (!destinationEntityHandler) {
      return of([]);
    }

    const requiredEntities: ISourceEntityHandler[] = values(
      destinationEntityHandler.sourceEntities ?? {}
    );

    if (!requiredEntities.length) {
      return of([]);
    }
    return safeCombineLatest(
      requiredEntities.map((requiredEntity) =>
        requiredEntity.getEntity$(migration)
      )
    ).pipe(
      map(compact),
      multiFilter(
        (requiredEntity) =>
          ![SourceEntityStatus.Locked].includes(requiredEntity.status)
      ),
      multiMap((requiredEntity) => requiredEntity.metadata.label)
    );
  }

  static getDestinationEntityMigrationBlockers$(
    migration: WithRef<IPracticeMigration>,
    destinationEntity: WithRef<IDestinationEntity>
  ): Observable<WithRef<IDestinationEntity>[]> {
    const destinationEntityHandler =
      DestinationHandler.getDestinationEntityHandlerByKey(
        migration,
        destinationEntity.metadata.key
      );

    if (!destinationEntityHandler) {
      return of([]);
    }

    const requiredEntities: IDestinationEntityHandler[] = values(
      destinationEntityHandler.destinationEntities
    );

    if (!requiredEntities.length) {
      return of([]);
    }

    return safeCombineLatest(
      requiredEntities.map((requiredEntity) =>
        requiredEntity.getEntity$(migration)
      )
    ).pipe(
      map(compact),
      multiFilter(
        (requiredEntity) =>
          ![DestinationEntityStatus.Locked].includes(requiredEntity.status)
      )
    );
  }

  static getAllDestinationEntityBlockers$(
    migration: WithRef<IPracticeMigration>,
    destinationEntity: WithRef<IDestinationEntity>
  ): Observable<string[]> {
    return safeCombineLatest([
      DestinationHandler.getDestinationEntityMigrationSourceBlockers$(
        migration,
        destinationEntity
      ),
      DestinationHandler.getDestinationEntityMigrationBlockers$(
        migration,
        destinationEntity
      ).pipe(multiMap((entity) => entity.metadata.label)),
    ]).pipe(map(flatten));
  }

  static async getOrderedDestinationEntityMigration(
    migration: WithRef<IPracticeMigration>
  ): Promise<WithRef<IDestinationEntity>[]> {
    const destinationHandler =
      DestinationHandler.getDestinationHandler(migration);
    if (!destinationHandler) {
      throw new Error(`DestinationHandler not found: ${migration.ref.path}`);
    }

    const destinationEntities = await Firestore.getDocs(
      PracticeMigration.destinationEntityCol(migration),
      orderBy('metadata.key')
    );

    const destinationEntityWithDependencies = await asyncForEach(
      destinationEntities.filter(
        (entity) => entity.status !== DestinationEntityStatus.Locked
      ),
      async (destinationEntity) => {
        return {
          destinationEntity,
          dependencies: await snapshot(
            DestinationHandler.getDestinationEntityMigrationBlockers$(
              migration,
              destinationEntity
            )
          ),
        };
      }
    );

    return new DestinationDependencyOrderer(
      destinationEntityWithDependencies
    ).getOrderedResults();
  }
}

interface IDestinationEntityWithDependencies {
  destinationEntity: WithRef<IDestinationEntity>;
  dependencies: WithRef<IDestinationEntity>[];
}

class DestinationDependencyOrderer {
  private _visitedDestinations = new Set<string>();
  private _orderedDestinationEntities: WithRef<IDestinationEntity>[] = [];
  private _entityMap = new Map<string, IDestinationEntityWithDependencies>();

  constructor(
    private _destinationEntityWithDependencies: IDestinationEntityWithDependencies[]
  ) {}

  getOrderedResults(): WithRef<IDestinationEntity>[] {
    this._destinationEntityWithDependencies.map(
      ({ destinationEntity, dependencies }) =>
        this._entityMap.set(destinationEntity.ref.id, {
          destinationEntity: destinationEntity,
          dependencies,
        })
    );

    for (const { destinationEntity } of this
      ._destinationEntityWithDependencies) {
      this.recursivelyResolveDestinationEntityDependencies(
        destinationEntity.ref.id
      );
    }

    return this._orderedDestinationEntities;
  }

  recursivelyResolveDestinationEntityDependencies(entityId: string): void {
    if (this._visitedDestinations.has(entityId)) {
      return;
    }

    const entry = this._entityMap.get(entityId);
    if (entry) {
      for (const dependency of entry.dependencies) {
        this.recursivelyResolveDestinationEntityDependencies(dependency.ref.id);
      }
      this._visitedDestinations.add(entityId);
      this._orderedDestinationEntities.push(entry.destinationEntity);
    }
  }
}
